// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, AppIcon, Button, IconButton, Spinner } from '@loa/ui-components';
import { colors } from '@loa/design-tokens';
import {
  relationshipCashflowApi,
  type RelationshipCashflow,
  type Scenario,
} from '../../api/relationshipCashflowApi';
import { clientApi } from '../../api/clientApi';
import { CreateForecastModal } from './CreateForecastModal';

/**
 * The relationship page's Cashflow tab (docs/specs/cashflow-modelling-tab-prd.md
 * R1–R4): the forecast list (name / clients / basis / last updated / actions), a
 * base-projection strip linking to the workspace in base mode, and the Create
 * New Forecast modal. Renders a "not enabled" empty state when the cashflow
 * feature is off (404 → null).
 */

function formatUpdated(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-IE', { dateStyle: 'short', timeStyle: 'short' });
}

export function CashflowForecastTab({ profileId }: { profileId: string }) {
  const navigate = useNavigate();
  const [base, setBase] = useState<RelationshipCashflow | null>(null);
  const [disabled, setDisabled] = useState(false);
  const [scenarios, setScenarios] = useState<Scenario[]>([]);
  const [clientNames, setClientNames] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      relationshipCashflowApi.getCashflow(profileId),
      relationshipCashflowApi.listScenarios(profileId),
    ])
      .then(([cashflow, list]) => {
        if (cashflow === null) setDisabled(true);
        else setBase(cashflow);
        setScenarios(list);
      })
      .catch(() => setError('Could not load the cashflow forecasts.'))
      .finally(() => setLoading(false));
  }, [profileId]);

  useEffect(load, [load]);

  useEffect(() => {
    clientApi
      .getClientsByProfile(profileId)
      .then((clients) => setClientNames(clients.map((c) => `${c.firstName} ${c.lastName}`.trim())))
      .catch(() => setClientNames([]));
  }, [profileId]);

  const clientsLabel = useMemo(() => clientNames.join(', '), [clientNames]);
  const taxAvailable = base?.basis === 'GROSS';

  const openWorkspace = (scenarioId?: string) => {
    navigate(
      scenarioId
        ? `/relationships/${profileId}/cashflow/${scenarioId}`
        : `/relationships/${profileId}/cashflow`,
    );
  };

  const remove = (scenarioId: string) => {
    if (deletingId) return;
    setDeletingId(scenarioId);
    relationshipCashflowApi
      .deleteScenario(profileId, scenarioId)
      .then(load)
      .catch(() => setError('Could not delete the forecast.'))
      .finally(() => setDeletingId(null));
  };

  if (disabled) {
    return (
      <div
        data-testid="cashflow-tab-disabled"
        className="bg-white rounded-card p-lg text-body-03 text-grey-03"
      >
        Cashflow modelling isn't enabled for your brokerage.
      </div>
    );
  }

  return (
    <div data-testid="cashflow-forecast-tab" className="bg-white rounded-card p-lg">
      <div className="flex items-center justify-between mb-md">
        <h3 className="text-body-01 font-semibold text-black m-0">Cashflow Modelling</h3>
        <Button size="md" data-testid="create-forecast-button" onClick={() => setCreating(true)}>
          <AppIcon name="plus" size={16} color={colors.white} />
          Create New Forecast
        </Button>
      </div>

      {error && <Alert variant="error">{error}</Alert>}
      {loading && <Spinner />}

      {!loading && base && (
        <button
          type="button"
          data-testid="cashflow-base-strip"
          onClick={() => openWorkspace()}
          className="w-full text-left flex items-center justify-between rounded-lg border border-grey-05 bg-lilac-06 px-md py-sm mb-md cursor-pointer hover:bg-lilac-06"
        >
          <span className="text-body-03 text-black">
            <strong>Base projection</strong> — {base.horizonYears}-year outlook
            {base.basis === 'GROSS' ? ', after Irish tax' : ', pre-tax'}
          </span>
          <span className="text-body-03 text-purple-primary">Open →</span>
        </button>
      )}

      {!loading && scenarios.length === 0 && (
        <div
          data-testid="forecast-empty-state"
          className="rounded-lg bg-lilac-06 px-md py-xl text-center text-body-03 text-grey-04"
        >
          No forecasts to show. Create a new forecast
        </div>
      )}

      {!loading && scenarios.length > 0 && (
        <table data-testid="forecast-table" className="w-full border-collapse">
          <thead>
            <tr className="bg-lilac-06 text-left">
              <th className="px-sm py-sm text-body-03 font-medium text-grey-01">Forecast Name</th>
              <th className="px-sm py-sm text-body-03 font-medium text-grey-01">Client(s)</th>
              <th className="px-sm py-sm text-body-03 font-medium text-grey-01">Basis</th>
              <th className="px-sm py-sm text-body-03 font-medium text-grey-01">Last updated</th>
              <th className="px-sm py-sm text-body-03 font-medium text-grey-01">Action</th>
            </tr>
          </thead>
          <tbody>
            {scenarios.map((s) => (
              <tr key={s.id} data-testid="forecast-row" className="border-b border-grey-06">
                <td className="px-sm py-sm">
                  <Button
                    variant="link"
                    size="sm"
                    data-testid="forecast-open"
                    onClick={() => openWorkspace(s.id)}
                  >
                    {s.name}
                  </Button>
                </td>
                <td className="px-sm py-sm text-body-03 text-grey-01">{clientsLabel || '—'}</td>
                <td className="px-sm py-sm text-body-03 text-grey-01">
                  {s.basis === 'GROSS' ? 'Gross' : 'Net'}
                </td>
                <td className="px-sm py-sm text-body-03 text-grey-01">{formatUpdated(s.updatedAt)}</td>
                <td className="px-sm py-sm">
                  <div className="flex items-center gap-sm">
                    <IconButton
                      variant="ghost"
                      size="sm"
                      data-testid="forecast-edit"
                      aria-label={`Open ${s.name}`}
                      onClick={() => openWorkspace(s.id)}
                      icon={<AppIcon name="edit" size={16} color={colors.purplePrimary} />}
                    />
                    <IconButton
                      variant="ghost"
                      size="sm"
                      data-testid="forecast-delete"
                      aria-label={`Delete ${s.name}`}
                      disabled={deletingId === s.id}
                      onClick={() => remove(s.id)}
                      icon={<AppIcon name="trash" size={16} color={colors.grey03} />}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {creating && (
        <CreateForecastModal
          profileId={profileId}
          clientNames={clientNames}
          taxAvailable={taxAvailable}
          onClose={() => setCreating(false)}
          onCreated={(s) => {
            setCreating(false);
            openWorkspace(s.id);
          }}
        />
      )}
    </div>
  );
}

export default CashflowForecastTab;
